import axios from "axios";
import getErrorMessageFromErrorCode from "Constants/ServerErrors";
import { ERROR_CODE, METHOD, HTTP_STATUS } from "Communication/Constants";

// constants
import {
  URLS,
  LOGOUT_URL,
  PROCESS_URL,
  GENERATE_ACCESS_TOKEN,
} from "./ApiUrls";

// error lookup
import errorLookup from "./ErrorLookup";

import BaseApi from "./BaseApi";
import { isInBranchFlow, showError } from "../Utils/LogicUtilities";
import {
  EXPIRES_AT,
  REFRESH_EXPIRES_AT,
  REFRESH_TRIGGER_SECONDS,
  S_MSISDN,
  S_EMAIL,
  TOKEN_KEY,
  S_STAFF_MSISDN,
  S_STAFF_EMAIL,
} from "./Constants";
import RefreshTokenRequest from "./ApiClasses/RefreshTokenRequest";
import { refreshToken } from "../Components/Signin/Actions/SigninAction";
import AppConstants from "../Constants/AppConstants";
import {
  signalTokenRefreshStart,
  signalTokenRefreshComplete,
  signalTokenRefreshError,
} from "../Redux/Sagas/SagaCoordinator";

export default class Axios extends BaseApi {
  constructor(showErrorModal, generateAccessToken, goToLoginPage) {
    super();
    this.showErrorModal = showErrorModal;
    this.generateAccessToken = generateAccessToken;
    this.goToLoginPage = goToLoginPage;
    this.axios = axios.create({
      baseURL: URLS.BASE_URL,
    });

    const getNewToken = async (currentAuth) => {
      const response = await fetch(GENERATE_ACCESS_TOKEN, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: currentAuth,
        },
        body: JSON.stringify({
          token: sessionStorage.getItem(TOKEN_KEY),
        }),
      });
      const json = await response.json();
      return json;
    };

    this.requestInterceptor = this.axios.interceptors.request.use(
      async (request) => {
        console.log("request interceptor", request);
        const currentAuth = "Bearer " + sessionStorage.getItem(TOKEN_KEY);

        if (!request.headers.Authorization) {
          return request;
        } else if (request.headers.Authorization !== currentAuth) {
          request.headers.Authorization = currentAuth;
          return request;
        } else {
          const expiresAt = sessionStorage.getItem(EXPIRES_AT);
          const refreshExpiresAt = sessionStorage.getItem(REFRESH_EXPIRES_AT);
          const expireTime = Number(expiresAt);
          const refreshExpireTime = Number(refreshExpiresAt);
          console.log("expireTime", expireTime);
          console.log("refreshExpireTime", refreshExpireTime);
          const d = new Date().getTime();
          const currentTime = Math.floor(d / 1000);
          console.log("currentTime", currentTime);

          const timeToRefresh = expireTime - currentTime;
          console.log("timeToRefresh", timeToRefresh);

          // Wait for token refresh to complete if it's already in progress
          if (window.refreshingToken) {
            console.log("Token refresh in progress, waiting...");
            while (window.refreshingToken) {
              await new Promise((resolve) => setTimeout(resolve, 100)); // Wait 100ms before checking again
            }
            console.log("Token refresh completed, updating request header");
            // Update the request header with the new token
            const newToken = sessionStorage.getItem(TOKEN_KEY);
            request.headers.Authorization = "Bearer " + newToken;
            return request;
          }

          if (REFRESH_TRIGGER_SECONDS > timeToRefresh) {
            if (currentTime < refreshExpireTime) {
              console.log("refreshing token");
              window.refreshingToken = true;

              // Signal that token refresh has started - this will block all other Redux sagas
              signalTokenRefreshStart();

              try {
                let res = await fetch(GENERATE_ACCESS_TOKEN, {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                    Authorization: currentAuth,
                  },
                  body: JSON.stringify({
                    token: sessionStorage.getItem(TOKEN_KEY),
                  }),
                })
                  .then((res) => res.json())
                  .then((res) => {
                    console.log(res);
                    window.refreshingToken = false;
                    if (res.token) {
                      sessionStorage.setItem(TOKEN_KEY, res.token);
                      if (res.bankingType === AppConstants.BANKING_TYPE.STAFF) {
                        sessionStorage.setItem(S_STAFF_MSISDN, res.msisdn);
                        sessionStorage.setItem(S_STAFF_EMAIL, res.email);
                      } else {
                        sessionStorage.setItem(S_MSISDN, res.msisdn);
                        sessionStorage.setItem(S_EMAIL, res.email);
                      }
                      sessionStorage.setItem(EXPIRES_AT, res.expiresAt);
                      sessionStorage.setItem(
                        REFRESH_EXPIRES_AT,
                        res.refreshExpiresAt
                      );
                      request.headers.Authorization = "Bearer " + res.token;
                      console.log(
                        "updated header",
                        request.headers.Authorization
                      );

                      // Signal that token refresh has completed successfully
                      signalTokenRefreshComplete();
                      return request;
                    } else if (
                      res.code === ERROR_CODE.NO_ACTIVITY ||
                      res.code === ERROR_CODE.USER_DUPLICATE_LOGIN
                    ) {
                      // Signal that token refresh has failed
                      signalTokenRefreshError();
                      this.showErrorModal(
                        res.code,
                        getErrorMessageFromErrorCode(res.code, res.data)
                      );
                      return Promise.reject(res);
                    } else {
                      // Signal that token refresh has failed for unexpected response
                      signalTokenRefreshError();
                      return request;
                    }
                  });
              } catch (error) {
                console.log("error", error);
                window.refreshingToken = false;
                // Signal that token refresh has failed due to exception
                signalTokenRefreshError();
                throw error;
              }
            } else if (
              !request.url.endsWith("logout") &&
              !request.url.endsWith("passwordcriteria")
            ) {
              console.log(
                "no activity",
                getErrorMessageFromErrorCode(ERROR_CODE.NO_ACTIVITY, {})
              );
              this.showErrorModal(
                ERROR_CODE.NO_ACTIVITY,
                getErrorMessageFromErrorCode(ERROR_CODE.NO_ACTIVITY, {})
              );
            }
          } else {
            return request;
          }
        }
        console.log("exiting interceptor");
        return request;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    this.responseInterceptor = this.axios.interceptors.response.use(
      (res) => {
        console.log("response interceptor", res);
        if (res.data.error) {
          const errorObj = errorLookup(res.data.error);
          this.showErrorModal(errorObj.code, errorObj.message);
          return errorObj;
        }
        res.isError = false;
        return res;
      },

      (error) => {
        if (error.response?.config.url === PROCESS_URL) {
          return Promise.reject(error);
        }

        // Handle 413 errors that might not have a proper response object (network-level errors)
        // When CORS blocks a 413 error, it becomes a generic "Network Error"
        // Check if this is a file upload request that failed with a network error
        const isFileUploadRequest =
          error.config &&
          error.config.url &&
          error.config.url.includes("/files/upload");
        const isNetworkError =
          error.message === "Network Error" && !error.response;

        const is413Error =
          (error.message && error.message.includes("413")) ||
          error.status === 413 ||
          (error.response &&
            error.response.status === HTTP_STATUS.REQUEST_ENTITY_TOO_LARGE) ||
          (error.code === "ERR_BAD_RESPONSE" &&
            error.message &&
            error.message.includes("413")) ||
          (!error.response &&
            error.request &&
            error.message &&
            error.message.toLowerCase().includes("request entity too large")) ||
          (isFileUploadRequest && isNetworkError);

        if (is413Error) {
          this.showErrorModal(
            HTTP_STATUS.REQUEST_ENTITY_TOO_LARGE.toString(),
            getErrorMessageFromErrorCode(
              HTTP_STATUS.REQUEST_ENTITY_TOO_LARGE.toString(),
              {}
            )
          );
          return Promise.reject(error);
        }

        if (error.response) {
          console.log(error.response);

          if (
            error.response.status === HTTP_STATUS.BAD_REQUEST ||
            error.response.status === HTTP_STATUS.UNPROCESSABLE_ENTITY
          ) {
            if (error.response.config.url.includes("refresh")) {
              // do nothing
              // TODO need to handle this
            } else {
              if (
                ERROR_CODE.DATA_NOT_AVAILABLE !== error.response.data?.code &&
                ERROR_CODE.FAIL_ASSERTION !== error.response.data?.code &&
                ERROR_CODE.DOCS_STILL_CREATING !== error.response.data?.code
              ) {
                if (showError(error)) {
                  this.showErrorModal(
                    error.response.data.code,
                    getErrorMessageFromErrorCode(
                      error.response.data.code,
                      error.response.data
                    )
                  );
                  if (
                    isInBranchFlow() &&
                    error.response.config.url.endsWith("profile/v1/login") &&
                    error.response.data.code === ERROR_CODE.OTP_EXPIRES
                  ) {
                    return error.response;
                  }
                } else {
                  return error.response;
                }
              } else {
                return error.response;
              }
            }
          } else if (
            error.response.status === HTTP_STATUS.INTERNAL_SERVER_ERROR
          ) {
            /**
             * DATA_NOT_AVAILABLE: When user scan document it takes time to get data back from server
             * DOCS_STILL_CREATING: When sending docs but are still being created
             * So we make a call one after another until we receive some data from backend, we do not show error modal for each call.
             */
            if (
              ERROR_CODE.DATA_NOT_AVAILABLE !== error.response.data.code &&
              ERROR_CODE.FAIL_ASSERTION !== error.response.data.code &&
              ERROR_CODE.DOCS_STILL_CREATING !== error.response.data.code
            ) {
              if (showError(error)) {
                this.showErrorModal(
                  error.response.data.code,
                  getErrorMessageFromErrorCode(
                    HTTP_STATUS.INTERNAL_SERVER_ERROR,
                    error.response.data
                  )
                );
              }
            }
          } else if (
            error.response.status === HTTP_STATUS.REQUEST_ENTITY_TOO_LARGE
          ) {
            this.showErrorModal(
              HTTP_STATUS.REQUEST_ENTITY_TOO_LARGE.toString(),
              getErrorMessageFromErrorCode(
                HTTP_STATUS.REQUEST_ENTITY_TOO_LARGE.toString(),
                {}
              )
            );
          } else if (error.response.status === HTTP_STATUS.NOT_FOUND) {
            /**
             * Developer information print in log
             * User pop up should have some meaningfull message. add in server errors
             */
            console.log(`Current URL: ${error.response.config.url}.`);
            this.showErrorModal(
              HTTP_STATUS.NOT_FOUND,
              getErrorMessageFromErrorCode(HTTP_STATUS.NOT_FOUND.toString(), {})
            );
          } else if (
            error.response.status === HTTP_STATUS.FORBIDDEN ||
            error.response.status === HTTP_STATUS.UNAUTHORISED
          ) {
            if (error.response.config.url === LOGOUT_URL) {
              if (!error.response.config.data.indexOf("dontSignIn") > 0) {
                console.log("going to login page", error.response.config.data);
                this.goToLoginPage(true);
              }
            } else {
              this.showErrorModal(
                "100011",
                "Your session has expired. Please sign in to continue."
              );
            }
          } else {
            if (showError(error)) {
              this.showErrorModal(
                error.response.data.code,
                getErrorMessageFromErrorCode(
                  error.response.data.code,
                  error.response.data
                )
              );
            } else {
              return error;
            }
          }
        } else {
          this.showErrorModal(0, error.toString());
        }
        return Promise.reject(error);
      }
    );
  }

  callApi(method, url, requestBody, accessToken) {
    const config = {
      headers: {
        "content-type": "application/json",
      },
      method,
      url,
      // timeout: TIMEOUT_DURATION,
    };
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }

    config[method !== METHOD.HTTP.GET ? "data" : "params"] = requestBody;
    return this.axios(config);
  }

  destructor = () => {
    this.axios.interceptors.response.eject(this.responseInterceptor);
  };
}
